import React, { useState, useRef, useCallback, useEffect } from 'react';
import { files } from './api';
import { sha256 } from 'js-sha256';
import { deleteOriginal, requestDeletePermissions } from './deleteOriginal';

// ---------- helpers ----------
const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
};
const formatSpeed = (bps) => {
  if (!bps || !isFinite(bps)) return '— MB/s';
  return `${(bps / (1024 * 1024)).toFixed(1)} MB/s`;
};
const formatETA = (s) => {
  if (!s || !isFinite(s) || s < 0) return '—';
  if (s < 1) return '<1s';
  if (s < 60) return `${Math.round(s)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
};
// v5.6.0: exported so the renewal workspace upload reuses the same helper.
export const computeSha256 = async (file) => {
  const buf = await file.arrayBuffer();
  return sha256(buf);
};
const supportsFSA = () => typeof window !== 'undefined' && 'showOpenFilePicker' in window;

// ---------- delete-original (v5.9.2 permissions; v5.10.1 reason, retry, freshness check) ----------
// The engine lives in ./deleteOriginal.js (pure, node-tested): up-front permission requests,
// remove() only after a server-sha256-matched upload, and — on remove()'s InvalidStateError /
// NotReadableError (Windows refused the delete: file open in Acrobat or the preview pane) — one
// retry after ~2 s, only if a fresh read still matches the uploaded size + sha256.

// ---------- preview type detection ----------
const getPreviewType = (file) => {
  if (!file) return 'none';
  const name = file.name.toLowerCase();
  const type = file.type || '';
  if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)) return 'image';
  if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  if (type.startsWith('text/') || /\.(txt|csv|json|xml|html?|md|log|yaml|yml|ini|conf|eml|msg)$/i.test(name)) return 'text';
  return 'other';
};

const getFileIcon = (file) => {
  if (!file) return '📄';
  const name = file.name.toLowerCase();
  if (/\.(doc|docx)$/.test(name)) return '📘';
  if (/\.(xls|xlsx|csv)$/.test(name)) return '📗';
  if (/\.(ppt|pptx)$/.test(name)) return '📙';
  if (/\.(zip|rar|7z|tar|gz)$/.test(name)) return '🗜️';
  if (/\.(mp3|wav|flac|m4a|aac)$/.test(name)) return '🎵';
  if (/\.(mp4|mov|avi|mkv|webm)$/.test(name)) return '🎬';
  if (/\.(eml|msg)$/.test(name)) return '✉️';
  return '📄';
};

class SpeedTracker {
  constructor() { this.samples = []; this.startedAt = null; }
  start() { this.startedAt = Date.now(); this.samples = [{ t: this.startedAt, loaded: 0 }]; }
  update(loaded) {
    const now = Date.now();
    this.samples.push({ t: now, loaded });
    const cutoff = now - 1000;
    this.samples = this.samples.filter((s) => s.t >= cutoff);
    if (this.samples.length < 2) this.samples.unshift({ t: cutoff, loaded });
  }
  currentSpeed() {
    if (this.samples.length < 2) return 0;
    const f = this.samples[0], l = this.samples[this.samples.length - 1];
    const dt = (l.t - f.t) / 1000;
    return dt <= 0 ? 0 : (l.loaded - f.loaded) / dt;
  }
  averageSpeed(total) {
    if (!this.startedAt) return 0;
    const dt = (Date.now() - this.startedAt) / 1000;
    return dt <= 0 ? 0 : total / dt;
  }
}

export default function UploadModal({ clientId, folderId, darkMode, onClose, onComplete }) {
  const [queue, setQueue] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [batchStats, setBatchStats] = useState({ totalSpeed: 0, eta: 0, completed: 0 });
  const [completionSummary, setCompletionSummary] = useState(null);
  const [selectedPreviewId, setSelectedPreviewId] = useState(null);
  const fileInputRef = useRef(null);
  const idCounter = useRef(0);
  const hasFSA = supportsFSA();

  // Theme colors
  const bg = darkMode ? '#1a1a1a' : '#ffffff';
  const panelBg = darkMode ? '#242424' : '#f5f5f5';
  const subPanelBg = darkMode ? '#1f1f1f' : '#eeeeee';
  const borderColor = darkMode ? '#333' : '#ddd';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';

  const addFiles = useCallback((entries) => {
    const newItems = entries.map(({ file, handle }) => ({
      id: ++idCounter.current,
      file,
      handle: handle || null,
      status: 'queued',
      loaded: 0,
      total: file.size,
      speed: 0,
      eta: 0,
      sha256: null,
      error: null,
      // Auto-checked by default; user can uncheck per file
      deleteAfterUpload: handle !== null, // only eligible if we have an FSA handle
      fileId: null,
      tracker: new SpeedTracker(),
    }));
    setQueue((q) => {
      const combined = [...q, ...newItems];
      // Auto-select first item for preview if nothing selected yet
      if (newItems.length > 0 && q.length === 0) {
        setSelectedPreviewId(newItems[0].id);
      }
      return combined;
    });
  }, []);

  const handleSelectFiles = async () => {
    if (hasFSA) {
      try {
        const handles = await window.showOpenFilePicker({ multiple: true });
        const entries = await Promise.all(handles.map(async (h) => ({ file: await h.getFile(), handle: h })));
        addFiles(entries);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Picker error:', err);
      }
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleFallbackInput = (e) => {
    const fileList = Array.from(e.target.files || []);
    addFiles(fileList.map((f) => ({ file: f, handle: null })));
    e.target.value = '';
  };

  const handlePaste = useCallback((e) => {
    const fileList = Array.from(e.clipboardData?.files || []);
    if (fileList.length) addFiles(fileList.map((f) => ({ file: f, handle: null })));
  }, [addFiles]);

  useEffect(() => {
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  const updateItem = (id, patch) => {
    setQueue((q) => q.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  };

  const toggleDeleteAfterUpload = (id) => {
    setQueue((q) => q.map((i) => (i.id === id ? { ...i, deleteAfterUpload: !i.deleteAfterUpload } : i)));
  };

  const removeFromQueue = (id) => {
    setQueue((q) => {
      const filtered = q.filter((i) => i.id !== id);
      // If we just removed the previewed file, pick another
      if (selectedPreviewId === id) {
        setSelectedPreviewId(filtered[0]?.id || null);
      }
      return filtered;
    });
  };

  // Delete one original whose upload is confirmed (server sha256 matched). Only runs remove() on a
  // handle that was GRANTED up front (re-checked first); never asks for a new permission. v5.10.1:
  // the engine (./deleteOriginal.js) retries once on a "file open elsewhere" error after a fresh
  // size + sha256 check. Every outcome — deleted, failed, or skipped — is logged via
  // files.logClientDelete; v5.10.2: a delete rescued by that retry is logged "success (after retry)".
  // fileId and the uploaded sha256 are passed in explicitly: the `item`
  // object is a pre-upload snapshot, so item.fileId / item.sha256 may be stale (null).
  const attemptDelete = async (item, fileId, perm, uploadedSha) => {
    const name = item.file.name;
    if (perm && perm.granted) updateItem(item.id, { status: 'deleting', error: null, fileId });
    const res = await deleteOriginal({
      handle: item.handle,
      perm,
      expected: { sha256: uploadedSha, size: item.file.size },
      hash: computeSha256,
    });
    if (res.outcome === 'deleted') {
      updateItem(item.id, { status: 'deleted', error: null, fileId });
      files.logClientDelete(fileId, name, true, null, res.retried).catch(() => {});
      return true;
    }
    updateItem(item.id, { status: res.outcome === 'skipped' ? 'delete_skipped' : 'delete_failed', error: res.reason, fileId });
    files.logClientDelete(fileId, name, false, res.log).catch(() => {});
    return false;
  };

  // Upload a single file. If deleteAfterUpload is true AND we have a handle, the original is
  // deleted after a successful, hash-matched upload — using the permission requested up front
  // in handleUploadAll (perm), never a new prompt here.
  const uploadOne = async (item, perm) => {
    updateItem(item.id, { status: 'hashing' });
    let sha;
    try {
      sha = await computeSha256(item.file);
      updateItem(item.id, { sha256: sha, status: 'uploading' });
      item.tracker.start();
    } catch (err) {
      updateItem(item.id, { status: 'failed', error: 'Hash failed' });
      return;
    }

    let uploadedOK = false;
    let uploadedFileId = null;
    let lastUiUpdate = 0;
    try {
      const res = await files.uploadV2(clientId, folderId, item.file, sha, (evt) => {
        item.tracker.update(evt.loaded);
        const now = Date.now();
        if (now - lastUiUpdate < 160 && evt.loaded < evt.total) return;
        lastUiUpdate = now;
        const speed = item.tracker.currentSpeed();
        const remaining = evt.total - evt.loaded;
        const eta = speed > 0 ? remaining / speed : 0;
        updateItem(item.id, { loaded: evt.loaded, total: evt.total, speed, eta });
      });
      if (res.data?.success && res.data?.sha256 === sha) {
        updateItem(item.id, {
          status: 'uploaded',
          loaded: item.file.size,
          speed: item.tracker.averageSpeed(item.file.size),
          eta: 0,
          fileId: res.data.file_id,
        });
        uploadedFileId = res.data.file_id;
        uploadedOK = true;
      } else {
        updateItem(item.id, { status: 'failed', error: 'Hash mismatch on server' });
      }
    } catch (err) {
      updateItem(item.id, {
        status: 'failed',
        error: err.response?.data?.detail || err.message || 'Upload failed',
      });
    }

    // Delete the original only after a successful, hash-matched upload (never on failure).
    if (uploadedOK && item.deleteAfterUpload && item.handle) {
      await attemptDelete(item, uploadedFileId, perm, sha);
    }
  };

  const handleUploadAll = async () => {
    setIsUploading(true); // FIRST: a second click cannot start a duplicate batch
    setCompletionSummary(null);
    const toUpload = queue.filter((item) => item.status === 'queued');
    // v5.9.2: ask every delete permission NOW, while the Upload click's activation is fresh —
    // before any hashing or upload.
    const perms = await requestDeletePermissions(
      toUpload.filter((item) => item.deleteAfterUpload && item.handle),
    );
    // Record each item's permission result (granted, or the reason it wasn't).
    Object.entries(perms).forEach(([id, p]) => {
      updateItem(Number(id), { deletePermission: p.granted ? 'granted' : p.reason });
    });
    const batchStart = Date.now();
    let totalBytes = 0;
    for (const item of toUpload) {
      await uploadOne(item, perms[item.id]);
      totalBytes += item.file.size;
    }
    const elapsed = (Date.now() - batchStart) / 1000;
    setCompletionSummary({
      count: toUpload.length,
      bytes: totalBytes,
      elapsed,
      avgSpeed: totalBytes / elapsed,
    });
    setIsUploading(false);
    onComplete?.();
  };

  // v5.9.2 recovery: originals that uploaded fine (hash-matched, so they have a fileId) and were
  // flagged for deletion, but were skipped or failed to delete. The button click gives fresh
  // activation, so the same up-front permission request + remove() can run for just these.
  const remainingOriginals = queue.filter((i) =>
    i.deleteAfterUpload && i.handle && i.fileId != null &&
    (i.status === 'delete_skipped' || i.status === 'delete_failed'));

  const handleDeleteRemaining = async () => {
    if (isUploading) return;
    setIsUploading(true); // FIRST: blocks a second click / a new batch while this runs
    const items = remainingOriginals;
    const perms = await requestDeletePermissions(items);
    for (const item of items) {
      await attemptDelete(item, item.fileId, perms[item.id], item.sha256);
    }
    setIsUploading(false);
  };

  useEffect(() => {
    if (!isUploading) return;
    const interval = setInterval(() => {
      const active = queue.filter((i) => i.status === 'uploading');
      const totalSpeed = active.reduce((s, i) => s + (i.speed || 0), 0);
      const totalRem = active.reduce((s, i) => s + Math.max(0, i.total - i.loaded), 0);
      const eta = totalSpeed > 0 ? totalRem / totalSpeed : 0;
      const completed = queue.filter((i) => i.status === 'uploaded' || i.status === 'deleted' || i.status === 'failed' || i.status === 'delete_failed' || i.status === 'delete_skipped').length;
      setBatchStats({ totalSpeed, eta, completed });
    }, 250);
    return () => clearInterval(interval);
  }, [isUploading, queue]);

  // Drag-drop
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (e) => {
    e.preventDefault();
    const fileList = Array.from(e.dataTransfer.files);
    addFiles(fileList.map((f) => ({ file: f, handle: null })));
  };

  const totalBytes = queue.reduce((s, i) => s + i.file.size, 0);
  const queuedCount = queue.filter((i) => i.status === 'queued').length;
  const allDone = queue.length > 0 && queue.every((i) => i.status !== 'queued' && i.status !== 'uploading' && i.status !== 'hashing' && i.status !== 'deleting');
  const selectedItem = queue.find((i) => i.id === selectedPreviewId);
  const willDeleteCount = queue.filter((i) => i.deleteAfterUpload && i.handle && i.status === 'queued').length;
  // v5.10.1: PDFs are the files Windows refuses to delete while they are open (Acrobat, preview pane).
  const willDeletePdf = queue.some((i) => i.deleteAfterUpload && i.handle && i.status === 'queued'
    && /\.pdf$/i.test(i.file?.name || ''));

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '5vh',
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} onDragOver={handleDragOver} onDrop={handleDrop}
        style={{
          width: '75vw', minWidth: 800, maxWidth: 1800, height: '85vh', maxHeight: '85vh', background: bg, color: textPrimary,
          border: `1px solid ${borderColor}`, borderRadius: 10, overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          display: 'flex', flexDirection: 'column',
        }}>
        {/* header */}
        <div style={{
          padding: '14px 18px', borderBottom: `1px solid ${borderColor}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Upload files</div>
            <div style={{ fontSize: 12, color: textSecondary, marginTop: 2 }}>
              Client #{clientId}{folderId ? ` · Folder #${folderId}` : ''}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: textSecondary, fontSize: 20, cursor: 'pointer', padding: 0,
          }}>×</button>
        </div>

        {/* body — two panes: queue on left, preview on right */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {/* LEFT PANE — file select + queue list */}
          <div style={{
            width: '30%', minWidth: 380, maxWidth: 480, padding: 18,
            borderRight: `1px solid ${borderColor}`,
            overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10,
          }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleSelectFiles} style={{
                flex: 1, padding: '10px 12px', fontSize: 13, cursor: 'pointer', fontWeight: 500,
                background: '#185FA5', color: '#fff', border: 'none', borderRadius: 6,
              }}>📁 Select files</button>
              <div style={{
                flex: 1, padding: '10px 12px', fontSize: 12, textAlign: 'center',
                border: `1px dashed ${borderColor}`, borderRadius: 6, color: textSecondary,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>Drag, drop, or paste</div>
            </div>

            <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFallbackInput} />

            {queue.length > 0 && (
              <>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  fontSize: 11, fontWeight: 500, color: textSecondary, marginTop: 4,
                }}>
                  <span>Queue ({queue.length} {queue.length === 1 ? 'file' : 'files'}) · {formatBytes(totalBytes)}</span>
                </div>
                {hasFSA && willDeleteCount > 0 && !allDone && (
                  <div style={{
                    padding: '6px 10px', background: subPanelBg, borderRadius: 5,
                    fontSize: 11, color: textSecondary,
                  }}>
                    {willDeleteCount} file{willDeleteCount === 1 ? '' : 's'} will be deleted from your computer after upload.
                    Uncheck individual files below to keep them.
                    {willDeletePdf && (
                      <div style={{ marginTop: 4, color: '#BA7517' }}>
                        Close these PDFs in Acrobat (and the Explorer preview pane) before uploading so the originals can be deleted.
                      </div>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {queue.map((item) => (
                    <FileRow
                      key={item.id}
                      item={item}
                      isSelected={item.id === selectedPreviewId}
                      onSelect={() => setSelectedPreviewId(item.id)}
                      onToggleDelete={toggleDeleteAfterUpload}
                      onRemove={removeFromQueue}
                      hasFSA={hasFSA}
                      panelBg={panelBg}
                      subPanelBg={subPanelBg}
                      textPrimary={textPrimary}
                      textSecondary={textSecondary}
                      darkMode={darkMode}
                    />
                  ))}
                </div>
              </>
            )}

            {isUploading && (
              <div style={{
                marginTop: 6, padding: '8px 12px', background: '#E6F1FB', color: '#185FA5',
                borderRadius: 6, fontSize: 11,
              }}>
                {batchStats.completed} of {queue.length} complete · {formatSpeed(batchStats.totalSpeed)} · ~{formatETA(batchStats.eta)} remaining
              </div>
            )}

            {completionSummary && !isUploading && (
              <div style={{
                marginTop: 6, padding: '8px 12px', background: '#EAF3DE', color: '#3B6D11',
                borderRadius: 6, fontSize: 11,
              }}>
                Uploaded {completionSummary.count} files ({formatBytes(completionSummary.bytes)}) in {completionSummary.elapsed.toFixed(1)}s · avg {formatSpeed(completionSummary.avgSpeed)}
              </div>
            )}

            {/* v5.9.2 recovery: delete originals that uploaded but were not deleted */}
            {hasFSA && !isUploading && remainingOriginals.length > 0 && (
              <button onClick={handleDeleteRemaining} style={{
                marginTop: 6, padding: '8px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 500,
                background: '#BA7517', color: '#fff', border: 'none', borderRadius: 6,
              }}>
                🗑 Delete {remainingOriginals.length} remaining original{remainingOriginals.length === 1 ? '' : 's'} from this PC
              </button>
            )}
          </div>

          {/* RIGHT PANE — preview */}
          <div style={{
            flex: 1, padding: 18, overflowY: 'auto',
            display: 'flex', flexDirection: 'column', background: subPanelBg,
          }}>
            {selectedItem ? (
              <FilePreview
                item={selectedItem}
                panelBg={panelBg}
                textPrimary={textPrimary}
                textSecondary={textSecondary}
                borderColor={borderColor}
              />
            ) : (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flex: 1, color: textSecondary, fontSize: 13, textAlign: 'center',
              }}>
                <div>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
                  Select files to see a preview here.<br />
                  <span style={{ fontSize: 11 }}>Click a file in the queue to preview it before uploading.</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* footer */}
        <div style={{
          padding: '12px 18px', borderTop: `1px solid ${borderColor}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <div style={{ fontSize: 11, color: textSecondary }}>
            {queue.length > 0 && `${queue.filter((i) => ['uploaded', 'deleted', 'deleting', 'delete_failed', 'delete_skipped'].includes(i.status)).length} of ${queue.length} uploaded`}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{
              padding: '6px 14px', fontSize: 12, cursor: 'pointer',
              background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 5,
            }}>{allDone ? 'Done' : 'Cancel'}</button>
            {!allDone && (
              <button onClick={handleUploadAll}
                disabled={isUploading || queuedCount === 0}
                style={{
                  padding: '6px 14px', fontSize: 12, cursor: isUploading || queuedCount === 0 ? 'not-allowed' : 'pointer',
                  background: '#185FA5', color: '#fff', border: 'none', borderRadius: 5, fontWeight: 500,
                  opacity: isUploading || queuedCount === 0 ? 0.5 : 1,
                }}>
                {isUploading ? 'Uploading…' : `Upload ${queuedCount} file${queuedCount === 1 ? '' : 's'}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// FileRow — left-pane queue item
// ============================================================
function FileRow({ item, isSelected, onSelect, onToggleDelete, onRemove, hasFSA, panelBg, subPanelBg, textPrimary, textSecondary, darkMode }) {
  const isEligibleForDelete = item.handle !== null;
  const canRemove = item.status === 'queued';
  const pct = item.total > 0 ? (item.loaded / item.total) * 100 : 0;

  let statusText, statusColor, indicator;
  switch (item.status) {
    case 'queued':
      statusText = `${formatBytes(item.total)} · Ready`;
      statusColor = textSecondary;
      indicator = <div style={{ width: 12, height: 12, borderRadius: '50%', border: `1.5px solid ${textSecondary}` }} />;
      break;
    case 'hashing':
      statusText = `${formatBytes(item.total)} · Verifying integrity…`;
      statusColor = '#185FA5';
      indicator = <div style={{ width: 12, height: 12, borderRadius: '50%', border: '1.5px solid #185FA5', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />;
      break;
    case 'uploading':
      statusText = `${formatBytes(item.loaded)} / ${formatBytes(item.total)} · ${formatSpeed(item.speed)} · ${formatETA(item.eta)}`;
      statusColor = '#185FA5';
      indicator = <div style={{ width: 12, height: 12, borderRadius: '50%', border: '1.5px solid #185FA5', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />;
      break;
    case 'uploaded':
      statusText = `${formatBytes(item.total)} · Uploaded (kept on computer)`;
      statusColor = '#3B6D11';
      indicator = <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#3B6D11', color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</div>;
      break;
    case 'deleted':
      statusText = `${formatBytes(item.total)} · Uploaded & deleted from computer`;
      statusColor = '#3B6D11';
      indicator = <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#3B6D11', color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</div>;
      break;
    case 'failed':
      statusText = `${formatBytes(item.total)} · ${item.error || 'Failed'}`;
      statusColor = '#A32D2D';
      indicator = <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#A32D2D', color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>!</div>;
      break;
    case 'deleting':
      statusText = `${formatBytes(item.total)} · Uploaded · deleting from computer…`;
      statusColor = '#185FA5';
      indicator = <div style={{ width: 12, height: 12, borderRadius: '50%', border: '1.5px solid #185FA5', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />;
      break;
    case 'delete_failed':
      statusText = `${formatBytes(item.total)} · Uploaded, not deleted — couldn't delete (${item.error})`;
      statusColor = '#BA7517';
      indicator = <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#BA7517', color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>!</div>;
      break;
    case 'delete_skipped':
      statusText = `${formatBytes(item.total)} · Uploaded, not deleted — ${item.error}`;
      statusColor = '#BA7517';
      indicator = <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#BA7517', color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>!</div>;
      break;
    default:
      statusText = '';
      statusColor = textSecondary;
      indicator = null;
  }

  const rowBg = isSelected ? (darkMode ? '#2a3a4a' : '#E6F1FB') : panelBg;
  const rowBorder = isSelected ? '#185FA5' : 'transparent';

  return (
    <div
      onClick={onSelect}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
        background: rowBg, borderRadius: 6, cursor: 'pointer',
        border: `1px solid ${rowBorder}`,
      }}
    >
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      {indicator}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {getFileIcon(item.file)} {item.file.name}
        </div>
        {item.status === 'uploading' && (
          <div style={{ marginTop: 3, height: 3, background: '#cfe2f3', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: '#185FA5', transition: 'width 0.15s' }} />
          </div>
        )}
        <div style={{ fontSize: 10, color: statusColor, marginTop: 2 }}>{statusText}</div>
      </div>

      {/* Delete-after-upload checkbox (only if eligible and still queued) */}
      {item.status === 'queued' && isEligibleForDelete && hasFSA && (
        <label
          onClick={(e) => e.stopPropagation()}
          title="Uncheck to keep this file on your computer after upload"
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            fontSize: 10, color: textSecondary, cursor: 'pointer',
            padding: '3px 6px', background: subPanelBg, borderRadius: 4, whiteSpace: 'nowrap',
          }}
        >
          <input
            type="checkbox"
            checked={item.deleteAfterUpload}
            onChange={() => onToggleDelete(item.id)}
            style={{ cursor: 'pointer', margin: 0 }}
          />
          <span>Delete after</span>
        </label>
      )}

      {canRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(item.id); }}
          title="Remove from queue"
          style={{
            background: 'none', border: 'none', color: textSecondary,
            fontSize: 16, cursor: 'pointer', padding: '0 4px', lineHeight: 1,
          }}
        >×</button>
      )}
    </div>
  );
}

// ============================================================
// FilePreview — right-pane preview
// ============================================================
function FilePreview({ item, panelBg, textPrimary, textSecondary, borderColor }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [textTruncated, setTextTruncated] = useState(false);
  const previewType = getPreviewType(item.file);

  useEffect(() => {
    let revokeUrl = null;
    setPreviewUrl(null);
    setTextContent(null);
    setTextTruncated(false);

    if (previewType === 'image' || previewType === 'pdf') {
      const url = URL.createObjectURL(item.file);
      setPreviewUrl(url);
      revokeUrl = url;
    } else if (previewType === 'text') {
      const LIMIT = 8000;
      const slice = item.file.slice(0, LIMIT);
      slice.text().then((txt) => {
        setTextContent(txt);
        if (item.file.size > LIMIT) setTextTruncated(true);
      }).catch(() => setTextContent('(could not read file)'));
    }

    return () => {
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [item.id, item.file, previewType]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* preview header */}
      <div style={{
        padding: '8px 12px', background: panelBg, borderRadius: 6,
        marginBottom: 10, fontSize: 12,
      }}>
        <div style={{ fontWeight: 500, color: textPrimary, wordBreak: 'break-word' }}>
          {getFileIcon(item.file)} {item.file.name}
        </div>
        <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>
          {formatBytes(item.file.size)} · {item.file.type || 'unknown type'}
          {item.file.lastModified ? ` · modified ${new Date(item.file.lastModified).toLocaleDateString()}` : ''}
        </div>
      </div>

      {/* preview body */}
      <div style={{
        flex: 1, minHeight: 0, background: panelBg, borderRadius: 6,
        overflow: 'hidden', display: 'flex', alignItems: 'stretch', justifyContent: 'stretch',
      }}>
        {previewType === 'image' && previewUrl && (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 10 }}>
            <img src={previewUrl} alt={item.file.name}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          </div>
        )}
        {previewType === 'pdf' && previewUrl && (
          <iframe src={previewUrl} title={item.file.name}
            style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }} />
        )}
        {previewType === 'text' && (
          <div style={{
            padding: 12, width: '100%', height: '100%', overflow: 'auto',
            fontFamily: 'SF Mono, Consolas, Monaco, monospace', fontSize: 11,
            color: textPrimary, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>
            {textContent === null ? <span style={{ color: textSecondary }}>Loading…</span> : textContent}
            {textTruncated && <div style={{ marginTop: 12, color: textSecondary, fontStyle: 'italic' }}>
              … file truncated; upload to see the rest.
            </div>}
          </div>
        )}
        {previewType === 'other' && (
          <div style={{
            width: '100%', height: '100%', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            flexDirection: 'column', gap: 12, color: textSecondary,
          }}>
            <div style={{ fontSize: 48 }}>{getFileIcon(item.file)}</div>
            <div style={{ fontSize: 12, textAlign: 'center', maxWidth: 300 }}>
              No preview available for this file type.<br />
              <span style={{ fontSize: 10 }}>
                Verify the filename and size above before uploading.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
